import styled from 'styled-components';

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
`;

export const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: ${(props) => props.theme.colors['neutral-100']};
`;

export const ScoreSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 32px;
`;

export const StatCard = styled.div`
  background: ${({ theme }) => theme.colors['neutral-0']};
  border: 2px solid ${({ theme }) => theme.colors['neutral-20']};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
`;

export const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const HealthScoreCard = styled.div`
  background: ${({ theme }) => theme.colors['neutral-0']};
  border: 2px solid ${({ theme }) => theme.colors['neutral-20']};
  border-radius: 12px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

export const ScoreRing = styled.div<{ $color: string }>`
  position: relative;
  width: 140px;
  height: 140px;

  svg {
    transform: rotate(-90deg);
  }

  .score-value {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 36px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    line-height: 1;
  }

  .score-label {
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translate(-50%, 8px);
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors['neutral-70']};
  }
`;

export const SeverityBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const SeverityRow = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${({ $color }) => `${$color}08`};
  border-left: 3px solid ${({ $color }) => $color};
`;

export const SeverityDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const SeverityLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors['neutral-90']};
  flex: 1;
`;

export const SeverityCount = styled.span<{ $color: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

export const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const CategoryCard = styled.div`
  background: ${({ theme }) => theme.colors['neutral-0']};
  border: 2px solid ${({ theme }) => theme.colors['neutral-20']};
  border-radius: 12px;
  padding: 24px;
`;

export const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const CategoryTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors['neutral-70']};
`;

export const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid hsla(0, 0%, 0%, 0.04);
  }
`;

export const CategoryLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors['neutral-90']};
  flex: 1;
`;

export const ProgressBar = styled.div`
  width: 120px;
  height: 6px;
  background: ${({ theme }) => theme.colors['neutral-20']};
  border-radius: 3px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 300ms cubic-bezier(0.2, 0.8, 0.4, 1);
`;

export const TopIssuesCard = styled.div`
  background: ${({ theme }) => theme.colors['neutral-0']};
  border: 2px solid ${({ theme }) => theme.colors['neutral-20']};
  border-radius: 12px;
  padding: 24px;
`;

export const IssueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid hsla(0, 0%, 0%, 0.04);
  }
`;

export const IssueInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const IssueTitle = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors['neutral-90']};
`;

export const IssueDescription = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors['neutral-60']};
  margin-top: 2px;
  line-height: 16px;
`;

export const IssueCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors['neutral-80']};
  flex-shrink: 0;
`;

export const RunningState = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

export const PulsingDot = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #054AD9;
  margin-bottom: 16px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.2); }
  }
`;
